// Evidence-photo lightbox, extracted from PhotoGallery (DetalleTienda
// "Estado visual", BIT-76 Piece B) so the postventa observation detail
// reuses it instead of importing the prototype's `.lbx` family.
//
// Native <dialog> + showModal()/close(): focus containment, Escape,
// top-layer stacking and focus restoration to the triggering tile come
// for free on this desktop-only surface. The `.vlb` classes and the
// --scrim-bg backdrop stay as-is (README §Decisiones 9).

import { useEffect, useRef, type ReactNode } from "react";
import { useLightboxNav } from "../lib/useLightboxNav";
import { Icon } from "./icons";

export type LightboxPhoto = {
  url: string;
  caption?: string | null;
  /** Area label shown above the caption and appended to the counter. */
  areaTag?: string | null;
  /** Explicit alt; defaults to caption, then a generic evidence label. */
  alt?: string;
  /** Optional pill in the footer (the store carousel's observation state). */
  badge?: ReactNode;
  /** Optional per-photo block under the meta (ADR-0169: the supervisor's
   *  decision controls). Rendered inside the dialog, so it stays
   *  reachable while the modal makes the rest of the page inert. Keyed by
   *  photo: navigating mounts the next photo's own footer. */
  footer?: ReactNode;
};

export type LightboxProps = {
  photos: LightboxPhoto[];
  openIndex: number | null;
  onClose: () => void;
  onNavigate: (i: number) => void;
  ariaLabel?: string;
  /** La foto no cargó (p. ej. una URL firmada que venció): quien monta el
   *  visor puede re-firmar y pasar `photos` nuevas. */
  onImageError?: (index: number) => void;
  /** Replaces the per-photo area tag after the counter («Foto 2 de 3 —
   *  {countLabel}») — one label for a whole set, e.g. an observation. */
  countLabel?: string | undefined;
  /** Polite announcement (e.g. «Foto 2 de 3: está bien. Queda 1 por
   *  revisar.»). When passed — even empty — a role=status region stays
   *  mounted for the dialog's life; omitted, no region is rendered. */
  status?: string | undefined;
  /** Assertive announcement (e.g. the observation was annulled). Same
   *  mount-always rule as `status`, with role=alert. */
  alert?: string | undefined;
};

export function Lightbox({
  photos,
  openIndex,
  onClose,
  onNavigate,
  ariaLabel = "Foto del reporte",
  onImageError,
  countLabel,
  status,
  alert,
}: LightboxProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const { prev, next, onKeyDown } = useLightboxNav(photos.length, openIndex, onNavigate);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (openIndex !== null && !dialog.open) {
      dialog.showModal();
    } else if (openIndex === null && dialog.open) {
      dialog.close();
    }
  }, [openIndex]);

  const current = openIndex !== null ? photos[openIndex] : undefined;
  const many = photos.length > 1;

  return (
    <dialog
      ref={dialogRef}
      className="vlb"
      aria-label={ariaLabel}
      onClose={onClose}
      onKeyDown={onKeyDown}
      // The dialog itself fills the viewport (inset:0); a click that lands
      // on it, not on a child, is a click on the scrim.
      onClick={(e) => {
        if (e.target === dialogRef.current) onClose();
      }}
    >
      {/* The live region stays mounted for the dialog's whole life (a
          region inserted together with its text is often not announced);
          it is empty while no photo is open. */}
      <div className="vlb__bar">
        <span className="vlb__count" aria-live="polite">
          {current
            ? `Foto ${(openIndex ?? 0) + 1} de ${photos.length}${
                (countLabel ?? current.areaTag) ? ` — ${countLabel ?? current.areaTag}` : ""
              }`
            : ""}
        </span>
        {current && (
          <button type="button" className="vlb__close" onClick={onClose} aria-label="Cerrar">
            <Icon.Close s={16} />
          </button>
        )}
      </div>
      {current && (
        <>
          <div className="vlb__stage">
            {many && (
              <button type="button" className="vlb__nav" onClick={prev} aria-label="Foto anterior">
                <Icon.ChevronLeft s={16} />
              </button>
            )}
            <div className="vlb__shot">
              <img
                src={current.url}
                onError={onImageError ? () => onImageError(openIndex ?? 0) : undefined}
                alt={
                  current.alt ??
                  current.caption ??
                  `Fotografía de evidencia${current.areaTag ? ` — ${current.areaTag}` : ""}`
                }
              />
            </div>
            {many && (
              <button type="button" className="vlb__nav" onClick={next} aria-label="Foto siguiente">
                <Icon.ChevronRight s={16} />
              </button>
            )}
          </div>
          <div className="vlb__meta">
            {current.areaTag && <div className="vlb__area">{current.areaTag}</div>}
            {current.caption && <div className="vlb__cap">{current.caption}</div>}
            {current.badge && <div className="vlb__badge">{current.badge}</div>}
          </div>
          {current.footer && (
            <div className="vlb__footer" key={`${openIndex}:${current.url}`}>
              {current.footer}
            </div>
          )}
        </>
      )}
      {status !== undefined && (
        <span className="sr-only" role="status">
          {status}
        </span>
      )}
      {alert !== undefined && (
        <span className="sr-only" role="alert">
          {alert}
        </span>
      )}
    </dialog>
  );
}
